import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { findTopLevelActiveItem, NavigationItemType } from 'framework/navigation';
import useHeaderMenuService from '../../headerMenuService';

export const MENU_PADDING = 40;
export const MENU_MARGIN = 40;

const useDesktopMainMenuItemsService = () => {
  const { getMenuData } = useHeaderMenuService();

  const { pathname } = useLocation();

  const data: NavigationItemType[] = useMemo(() => {
    const menu = getMenuData();
    const activeTopLevel = findTopLevelActiveItem(menu, pathname);
    return activeTopLevel?.thirdLevelMenuChildren ? [] : activeTopLevel?.children || [];
  }, [getMenuData, pathname]);

  const [visibleTabIndices, setVisibleTabIndices] = useState<string[]>([]);
  const [measuringRender, setMeasuringRender] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    setMeasuringRender(true);
  }, []);

  useLayoutEffect(() => {
    if (measuringRender && !visibleTabIndices.length) {
      const tabElements: HTMLElement[] = Array.from((containerRef?.current as any)?.children || []);
      let stopWidth = MENU_PADDING;
      const visible: string[] = [];
      const activeTab = tabElements.find(tab => tab?.querySelector('a.active'));
      const inactiveItems = tabElements.filter(tab => tab.id !== activeTab?.id);

      if (activeTab) {
        stopWidth += activeTab.getBoundingClientRect().width;
        visible.push(activeTab.id);
      }

      inactiveItems.forEach(tab => {
        stopWidth += tab.getBoundingClientRect().width;
        const temp = (containerRef.current as any)?.parentNode.getBoundingClientRect().width;
        if (temp - MENU_MARGIN >= stopWidth) {
          visible.push(tab.id);
        }
      });

      setVisibleTabIndices(visible);
      setMeasuringRender(false);
    }
  }, [measuringRender, visibleTabIndices]);

  useEffect(() => {
    function handleResize() {
      setVisibleTabIndices([]);
      setMeasuringRender(true);
    }

    window.addEventListener('resize', handleResize);

    return function cleanUp() {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (data.length) {
      setVisibleTabIndices([]);
      setMeasuringRender(true);
    }
  }, [data]);

  const visibleTabs = visibleTabIndices.length ? data.filter(({ url }) => visibleTabIndices.includes(url)) : data;

  return {
    visibleTabs,
    containerRef,
  };
};

export default useDesktopMainMenuItemsService;
